import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  LayoutDashboard, TrendingUp, Package, DollarSign, Settings, 
  BarChart3, Bell, ArrowRight, CheckCircle, Clock, Zap,
  AlertCircle, Crown, Sparkles, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const DashboardPage = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Check for payment success
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    if (status === 'success' && sessionId) {
      setShowSuccessMessage(true);
      // Clear URL params after showing message
      setTimeout(() => {
        window.history.replaceState({}, '', '/dashboard');
      }, 1000);
    }
  }, [searchParams]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login?redirect=/dashboard');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB]"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  // Calculate trial info
  const trialEndsAt = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const now = new Date();
  const daysRemaining = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24))) : 0;
  const isOnTrial = user.tier === 'freemium' && daysRemaining > 0;
  const trialExpired = user.tier === 'freemium' && daysRemaining <= 0;

  // Tier display names
  const tierDisplayNames = {
    freemium: 'Freemium',
    starter: 'Starter',
    growth: 'Growth',
    scale: 'Scale',
    enterprise: 'Enterprise+'
  };

  // Quick stats (placeholder data for demo)
  const quickStats = [
    { label: 'Products Tracked', value: '0', icon: Package, change: 'Setup required' },
    { label: 'Avg. Margin', value: '--%', icon: TrendingUp, change: 'No data yet' },
    { label: 'Revenue Impact', value: '$0', icon: DollarSign, change: 'Start tracking' },
    { label: 'Price Alerts', value: '0', icon: Bell, change: 'None active' }
  ];

  // Feature access based on tier
  const features = [
    { name: 'Price Optimization', available: true, icon: Zap },
    { name: 'Competitor Monitoring', available: user.tier !== 'freemium', icon: BarChart3 },
    { name: 'AI Recommendations', available: ['growth', 'scale', 'enterprise'].includes(user.tier), icon: Sparkles },
    { name: 'Custom Integrations', available: ['scale', 'enterprise'].includes(user.tier), icon: RefreshCw }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-8 bg-green-500/10 border border-green-500/30 rounded-xl p-6 flex items-center gap-4">
              <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-green-400">Payment Successful!</h3>
                <p className="text-[#94A3B8]">Your subscription has been activated. Welcome to PriceStrike!</p>
              </div>
            </div>
          )}

          {/* Trial Warning */}
          {trialExpired && (
            <div className="mb-8 bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-red-400">Trial Expired</h3>
                  <p className="text-[#94A3B8]">Upgrade now to continue using PriceStrike features.</p>
                </div>
              </div>
              <Link to="/pricing">
                <Button className="bg-red-500 hover:bg-red-600 text-white">
                  Upgrade Now
                </Button>
              </Link>
            </div>
          )}

          {/* Welcome Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-2">
                Welcome to PriceStrike
              </h1>
              <p className="text-xl text-[#94A3B8]">
                Your pricing intelligence dashboard
              </p>
            </div>
            
            {/* Plan Badge */}
            <div className="mt-6 md:mt-0 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Crown className={`w-6 h-6 ${user.tier === 'freemium' ? 'text-[#94A3B8]' : 'text-[#2563EB]'}`} />
                <div>
                  <p className="text-sm text-[#94A3B8]">Current Plan</p>
                  <p className="text-lg font-semibold text-[#F8FAFC]">
                    {tierDisplayNames[user.tier] || 'Freemium'}
                    {isOnTrial && <span className="text-[#2563EB] text-sm ml-2">({daysRemaining} days left)</span>}
                  </p>
                </div>
              </div>
              {user.tier === 'freemium' && (
                <Link to="/pricing" className="mt-3 block">
                  <Button size="sm" className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white">
                    Upgrade Plan
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {quickStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#2563EB]/10 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <span className="text-sm text-[#94A3B8]">{stat.label}</span>
                  </div>
                  <p className="text-3xl font-bold text-[#F8FAFC] mb-1">{stat.value}</p>
                  <p className="text-xs text-[#94A3B8]">{stat.change}</p>
                </div>
              );
            })}
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Getting Started */}
            <div className="lg:col-span-2 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-3">
                <LayoutDashboard className="w-6 h-6 text-[#2563EB]" />
                Getting Started
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/10">
                  <div className="w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center text-white font-semibold text-sm">1</div>
                  <div className="flex-1">
                    <h3 className="text-[#F8FAFC] font-semibold mb-1">Connect Your Data</h3>
                    <p className="text-[#94A3B8] text-sm mb-3">Import your product catalog, costs, and current pricing.</p>
                    <Button variant="outline" size="sm" className="border-[#2563EB]/50 text-[#2563EB] hover:bg-[#2563EB]/10">
                      Import Products
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/10">
                  <div className="w-8 h-8 bg-[#2563EB]/30 rounded-full flex items-center justify-center text-[#2563EB] font-semibold text-sm">2</div>
                  <div className="flex-1">
                    <h3 className="text-[#F8FAFC] font-semibold mb-1">Set Your Goals</h3>
                    <p className="text-[#94A3B8] text-sm mb-3">Define your margin targets and pricing rules.</p>
                    <Button variant="outline" size="sm" className="border-[#2563EB]/50 text-[#2563EB] hover:bg-[#2563EB]/10">
                      Configure Goals
                    </Button>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/10">
                  <div className="w-8 h-8 bg-[#2563EB]/30 rounded-full flex items-center justify-center text-[#2563EB] font-semibold text-sm">3</div>
                  <div className="flex-1">
                    <h3 className="text-[#F8FAFC] font-semibold mb-1">Get Recommendations</h3>
                    <p className="text-[#94A3B8] text-sm mb-3">Let PriceStrike analyze and optimize your pricing.</p>
                    <Button variant="outline" size="sm" className="border-[#2563EB]/50 text-[#2563EB] hover:bg-[#2563EB]/10" disabled>
                      Coming Soon
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Access */}
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-3">
                <Zap className="w-6 h-6 text-[#2563EB]" />
                Your Features
              </h2>
              
              <div className="space-y-3">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${feature.available ? 'bg-green-500/10' : 'bg-[#0A0F1C]/50'}`}>
                      <Icon className={`w-5 h-5 ${feature.available ? 'text-green-400' : 'text-[#94A3B8]'}`} />
                      <span className={feature.available ? 'text-[#F8FAFC]' : 'text-[#94A3B8]'}>{feature.name}</span>
                      {feature.available ? (
                        <CheckCircle className="w-4 h-4 text-green-400 ml-auto" />
                      ) : (
                        <span className="text-xs text-[#94A3B8] ml-auto">Upgrade</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {user.tier === 'freemium' && (
                <Link to="/pricing" className="mt-6 block">
                  <Button className="w-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 text-white">
                    Unlock All Features
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Link to="/roi-calculator" className="group">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6 hover:border-[#2563EB]/50 transition-all">
                <BarChart3 className="w-8 h-8 text-[#2563EB] mb-3" />
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1">ROI Calculator</h3>
                <p className="text-sm text-[#94A3B8]">See your potential savings</p>
              </div>
            </Link>
            
            <Link to="/features" className="group">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6 hover:border-[#2563EB]/50 transition-all">
                <Sparkles className="w-8 h-8 text-[#2563EB] mb-3" />
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1">Explore Features</h3>
                <p className="text-sm text-[#94A3B8]">Discover what's possible</p>
              </div>
            </Link>
            
            <Link to="/contact" className="group">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6 hover:border-[#2563EB]/50 transition-all">
                <Settings className="w-8 h-8 text-[#2563EB] mb-3" />
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1">Get Help</h3>
                <p className="text-sm text-[#94A3B8]">Talk to our team</p>
              </div>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DashboardPage;
